/**
 * Хук поиска лицевых счетов по номеру телефона для неавторизованного пользователя.
 *
 * Выполняет поиск автоматически (без кнопки) по мере ввода телефона с debounce,
 * как только номер приведён к валидному формату +7XXXXXXXXXX. Использует
 * react-query для кэширования и управления состоянием запроса.
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { searchAccount, AccountSearchResult } from '../api/payment.api';

/**
 * Проверяет, что телефон в формате маски заполнен полностью.
 *
 * @param maskedPhone - телефон из поля ввода с маской (+7 (999) 999-99-99)
 * @returns true, если номер полностью введён
 */
const isPhoneComplete = (maskedPhone: string): boolean =>
  /^\+7\s\(\d{3}\)\s\d{3}-\d{2}-\d{2}$/.test(maskedPhone.trim());

/**
 * Приводит телефон из формата маски к формату +7XXXXXXXXXX.
 *
 * @param maskedPhone - телефон с маской
 * @returns нормализованный телефон или пустая строка, если номер неполный
 */
const normalizePhone = (maskedPhone: string): string => {
  const digits = maskedPhone.replace(/\D/g, '');
  if (digits.length !== 11 || !digits.startsWith('7')) {
    return '';
  }
  return `+${digits}`;
};

/**
 * Результат работы хука поиска счетов гостя.
 */
export interface UseGuestAccountSearchResult {
  /** Найденные счета (с балансом и маскированным адресом) */
  accounts: AccountSearchResult[];
  /** Идёт ли поиск */
  isSearching: boolean;
  /** Поиск завершён и счета не найдены (для валидного телефона) */
  notFound: boolean;
}

/**
 * Хук автоматического поиска счетов по телефону с debounce.
 *
 * @param maskedPhone - значение телефона из поля ввода (с маской)
 * @param debounceMs - задержка перед запросом после последнего изменения (мс)
 * @returns найденные счета и состояние поиска
 *
 * @example
 * ```tsx
 * const { accounts, isSearching, notFound } = useGuestAccountSearch(phoneValue);
 * ```
 */
export const useGuestAccountSearch = (
  maskedPhone: string,
  debounceMs = 500
): UseGuestAccountSearchResult => {
  // Debounce номера телефона, чтобы не слать запрос на каждое нажатие
  const [debouncedPhone, setDebouncedPhone] = useState('');

  useEffect(() => {
    const normalized = isPhoneComplete(maskedPhone) ? normalizePhone(maskedPhone) : '';
    const timer = setTimeout(() => setDebouncedPhone(normalized), debounceMs);
    return () => clearTimeout(timer);
  }, [maskedPhone, debounceMs]);

  const enabled = debouncedPhone.length > 0;

  const { data, isFetching } = useQuery({
    queryKey: ['guest-account-search', debouncedPhone],
    queryFn: () => searchAccount(debouncedPhone),
    enabled,
    // Счета редко меняются в рамках сессии оплаты — кэшируем на минуту
    staleTime: 60_000,
    retry: false,
  });

  return {
    accounts: data?.accounts ?? [],
    isSearching: enabled && isFetching,
    notFound: enabled && !isFetching && (data?.accounts?.length ?? 0) === 0,
  };
};
